import { useEffect } from 'react';

/**
 * Owner 2026-10-23 16:08: „jak klikam w liczbę … niech się automatycznie zaznacza zawartość”. A number field
 * (type="number", inputmode decimal/numeric — so also `NumberInput` and the sheet cells) selects all of its
 * content when it gets the focus, by click or Tab, so typing replaces the number.
 * Not: text, search, textarea, read-only, disabled, or anything inside `data-no-select-all` (dates, NIP, IBAN…).
 */
export const NO_SELECT_ALL = 'data-no-select-all';

export function isNumberField(el: Element | null): el is HTMLInputElement {
  if (!(el instanceof HTMLInputElement) || el.disabled || el.readOnly) return false;
  if (el.closest(`[${NO_SELECT_ALL}]`)) return false;
  if (el.type === 'number') return true;
  if (el.type !== 'text') return false;
  const mode = el.getAttribute('inputmode');
  return mode === 'decimal' || mode === 'numeric';
}

/** Installs the document-wide handler; returns the uninstall. */
export function installSelectNumbersOnFocus(doc: Document): () => void {
  // the field that the current mouse press is focusing (not one that already had the focus)
  let pressed: HTMLInputElement | null = null;

  const onMouseDown = (e: MouseEvent) => {
    const el = e.target as Element | null;
    pressed = isNumberField(el) && doc.activeElement !== el ? el : null;
  };
  const onFocusIn = (e: FocusEvent) => {
    if (isNumberField(e.target as Element | null)) (e.target as HTMLInputElement).select();
  };
  // Chrome / Safari: the press that gave the focus puts a caret on mouseup — select again, unless the person
  // dragged over part of the number (then keep that). A later click in the focused field places the caret.
  // Safari (owner 20:31 „niby zaznacza i od razu odznacza”): WebKit can still collapse the selection after mouseup
  // (on click, or on the next task) — so the field focused by this press is selected again on click and once more
  // right after, unless the person dragged over part of it or pressed again meanwhile.
  let justFocused: HTMLInputElement | null = null;
  let press = 0;
  const collapsedIn = (el: HTMLInputElement) => el.type === 'number' || el.selectionStart === el.selectionEnd;
  const reselect = (el: HTMLInputElement) => {
    if (doc.activeElement === el && collapsedIn(el)) el.select();
  };
  const onMouseUp = (e: MouseEvent) => {
    const el = pressed;
    pressed = null;
    if (!el || e.target !== el || doc.activeElement !== el) return;
    // a drag over part of the number keeps that part (and is still not collapsed on click, so it stays)
    justFocused = el;
    if (collapsedIn(el)) {
      e.preventDefault();
      el.select();
    }
    const mine = press;
    (doc.defaultView ?? window).setTimeout(() => {
      if (press === mine) reselect(el);
    }, 0);
  };
  const onClick = (e: MouseEvent) => {
    const el = justFocused;
    justFocused = null;
    if (el && e.target === el) reselect(el);
  };
  const onAnyMouseDown = () => {
    press += 1;
    justFocused = null;
  };

  // owner 20:32: „po wpisaniu i kliknięciu Enter niech się zatwierdzi — oba entery” (the main one and the numeric
  // keypad's, both `key === 'Enter'`): a number field outside a form that nobody handled Enter for (a sheet cell, a
  // field saved on leaving) is left, so its own blur saves it. In a form Enter submits as before.
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' || e.defaultPrevented || e.isComposing) return;
    const el = e.target as Element | null;
    if (!isNumberField(el) || el.form) return;
    e.preventDefault();
    el.blur();
  };

  doc.addEventListener('keydown', onKeyDown);
  doc.addEventListener('mousedown', onAnyMouseDown, true);
  doc.addEventListener('mousedown', onMouseDown, true);
  doc.addEventListener('focusin', onFocusIn);
  doc.addEventListener('mouseup', onMouseUp, true);
  doc.addEventListener('click', onClick, true);
  return () => {
    doc.removeEventListener('keydown', onKeyDown);
    doc.removeEventListener('mousedown', onAnyMouseDown, true);
    doc.removeEventListener('mousedown', onMouseDown, true);
    doc.removeEventListener('focusin', onFocusIn);
    doc.removeEventListener('mouseup', onMouseUp, true);
    doc.removeEventListener('click', onClick, true);
  };
}

/** Mounted once in the app frame (AppLayout). */
export function useSelectNumbersOnFocus(): void {
  useEffect(() => installSelectNumbersOnFocus(document), []);
}
